function parseVariantInventory(form) {
  const script = form?.querySelector('[data-ld-variant-inventory]');
  if (!script) return {};
  try {
    return JSON.parse(script.textContent);
  } catch {
    return {};
  }
}

function isVariantBackorder(info, quantity) {
  if (!info || info.inventory_management !== 'shopify' || info.inventory_policy !== 'continue') {
    return false;
  }
  const available = Number(info.inventory_quantity);
  const needed = Number(quantity) > 0 ? Number(quantity) : 1;
  return available < needed;
}

function leadTimeProperties(isBackorder) {
  if (!isBackorder) return {};
  const name = window.variantStrings?.leadTimeProperty;
  const value = window.variantStrings?.backorderLeadTime;
  if (!name || !value) return {};
  return { [name]: value };
}

function withLeadTime(item, isBackorder) {
  const properties = { ...(item.properties || {}), ...leadTimeProperties(isBackorder) };
  const cleaned = Object.fromEntries(Object.entries(properties).filter(([, value]) => value));
  if (Object.keys(cleaned).length) item.properties = cleaned;
  else delete item.properties;
  return item;
}

function syncLeadTimeProperty(root = document) {
  root.querySelectorAll('[data-ld-lead-time-property]').forEach((input) => {
    const form = input.form;
    if (!form) return;
    const variantId = form.querySelector('[name="id"]')?.value;
    const quantity = parseInt(form.elements.namedItem('quantity')?.value, 10) || 1;
    const inventory = parseVariantInventory(form);
    input.disabled = !isVariantBackorder(inventory[String(variantId)], quantity);
  });
}

window.ldSyncLeadTimeProperty = syncLeadTimeProperty;

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.syncLeadTimeProperty();
        this.form?.addEventListener('change', (event) => {
          if (event.target.name === 'id' || event.target.name === 'quantity') {
            this.syncLeadTimeProperty();
          }
        });
        this.form?.elements.namedItem('quantity')?.addEventListener('change', () => {
          this.syncLeadTimeProperty();
        });

        const info = this.closest('product-info');
        info?.addEventListener('change', (event) => {
          if (event.target.matches?.('[data-emmc-size-select]')) this.syncEmmcOperatingSystem();
          const group = event.target.closest?.('[data-required-extra-group]');
          if (!group || group.hasAttribute('hidden') || !this.requiredExtraGroupSatisfied(group)) return;
          group.removeAttribute('data-invalid');
          this.handleErrorMessage();
        });
        this.syncEmmcOperatingSystem();
      }

      syncEmmcOperatingSystem() {
        const group = this.extraRoot().querySelector('[data-emmc-os-group]');
        const select = group?.querySelector('[data-emmc-os-select]');
        if (!group || !select) return;

        let choices = [];
        this.extraRoot().querySelectorAll('[data-emmc-size-select]').forEach((sizeSelect) => {
          const option = sizeSelect.options[sizeSelect.selectedIndex];
          if (!option || option.disabled || !option.dataset.osChoices) return;
          choices = option.dataset.osChoices.split('||').map((label) => label.trim()).filter(Boolean);
        });

        if (!choices.length) {
          group.setAttribute('hidden', '');
          group.removeAttribute('data-invalid');
          select.dataset.choiceKey = '';
          select.replaceChildren(this.emmcOsPlaceholder());
          return;
        }

        group.removeAttribute('hidden');
        const choiceKey = choices.join('||');
        if (select.dataset.choiceKey === choiceKey) return;

        const previous = select.value;
        select.dataset.choiceKey = choiceKey;
        select.replaceChildren(this.emmcOsPlaceholder());
        choices.forEach((label) => {
          const option = document.createElement('option');
          option.value = label;
          option.dataset.propertyName = 'Operating System';
          option.dataset.propertyValue = label;
          option.textContent = label;
          select.append(option);
        });
        if (previous && choices.includes(previous)) select.value = previous;
      }

      emmcOsPlaceholder() {
        const option = document.createElement('option');
        option.value = '';
        option.selected = true;
        option.disabled = true;
        option.dataset.placeholder = 'true';
        option.textContent = 'Choose one…';
        return option;
      }

      extraRoot() {
        return this.closest('product-info') || document;
      }

      requiredExtraGroupSatisfied(group) {
        const select = group.querySelector('[data-extra-select]');
        if (select) {
          const option = select.options[select.selectedIndex];
          if (!option || option.disabled || option.dataset.placeholder === 'true') return false;
          return Boolean(option.dataset.variantId || option.dataset.propertyName);
        }
        return Boolean(group.querySelector('.ld-extras__checkbox:checked'));
      }

      missingRequiredExtraGroup() {
        const groups = this.extraRoot().querySelectorAll('[data-required-extra-group]');
        let missing = null;
        groups.forEach((group) => {
          if (group.hasAttribute('hidden')) {
            group.removeAttribute('data-invalid');
            return;
          }
          group.removeAttribute('data-invalid');
          if (!missing && !this.requiredExtraGroupSatisfied(group)) {
            group.setAttribute('data-invalid', 'true');
            missing = group;
          }
        });
        return missing;
      }

      checkedExtraInputs() {
        const radios = Array.from(
          this.extraRoot().querySelectorAll('[data-optional-extras] .ld-extras__checkbox:checked:not(:disabled)')
        );
        const selects = Array.from(
          this.extraRoot().querySelectorAll('[data-optional-extras] [data-extra-select]')
        )
          .map((select) => select.options[select.selectedIndex])
          .filter((option) => option && !option.disabled && (option.dataset.variantId || option.dataset.propertyName));
        return [...radios, ...selects];
      }

      syncLeadTimeProperty() {
        syncLeadTimeProperty(this);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const missingGroup = this.missingRequiredExtraGroup();
        if (missingGroup) {
          const name = missingGroup.dataset.groupName || 'an option';
          this.handleErrorMessage(`Choose ${name} before adding to cart.`);
          missingGroup.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }

        this.syncLeadTimeProperty();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const extraInputs = this.checkedExtraInputs();
        const extraVariantIds = [
          ...new Set(
            extraInputs
              .map((input) => Number(input.dataset.variantId))
              .filter((id) => id && String(id) !== String(variantId))
          ),
        ];
        const osSelect = this.extraRoot().querySelector('[data-emmc-os-select]');
        const osGroup = osSelect?.closest('[data-emmc-os-group]');
        const osValue = osGroup && !osGroup.hasAttribute('hidden') ? osSelect.value : '';
        const choiceProperties = {};
        extraInputs.forEach((input) => {
          if (input.dataset.variantId) return;
          if (input.closest('[data-emmc-os-select]')) return;
          const propertyName = input.dataset.propertyName;
          const propertyValue = input.dataset.propertyValue;
          if (propertyName && propertyValue) choiceProperties[propertyName] = propertyValue;
        });
        const useItemPayload = extraVariantIds.length > 0 || Object.keys(choiceProperties).length > 0;

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        let requestBody;
        if (useItemPayload) {
          const inventory = parseVariantInventory(this.form);
          const formProperties = {};
          for (const [key, value] of formData.entries()) {
            const match = /^properties\[(.+)\]$/.exec(key);
            if (match && value) formProperties[match[1]] = value;
          }
          const payload = {
            items: [
              withLeadTime(
                {
                  id: Number(variantId),
                  quantity,
                  properties: { ...formProperties, ...choiceProperties },
                },
                isVariantBackorder(inventory[String(variantId)], quantity)
              ),
              ...extraVariantIds.map((id) => {
                const checkbox = extraInputs.find((input) => Number(input.dataset.variantId) === id);
                const item = { id, quantity };
                if (osValue && checkbox?.dataset.osChoices) {
                  item.properties = { 'Operating System': osValue };
                }
                return withLeadTime(item, checkbox?.dataset.backorder === 'true');
              }),
            ],
          };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.cart?.querySelector('cart-drawer-items')?.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
